import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { PostCard } from '../components/PostCard';
import { AddToPlaylistButton } from '../components/AddToPlaylistButton';
import {
  Music, MapPin, Play, Pause, Users, Heart,
  Loader2, Share2, UserPlus, UserMinus, MessageSquare,
  Tag, Headphones, Disc3, X, Video, Radio,
  ChevronLeft, Clock, Copy, Check
} from 'lucide-react';

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function BeatCard({ beat, onPlay, isPlaying, onLike }) {
  const [liked, setLiked] = useState(false);
  const { token, isAuthenticated } = useAuth();

  const toggleLike = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${API}/beats/${beat.beat_id}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
      }
    } catch {}
  };

  return (
    <div
      data-testid={`beat-card-${beat.beat_id}`}
      className="group relative rounded-xl overflow-hidden bg-muted/20 border border-border hover:border-orange-500/30 transition-all duration-300"
    >
      <div className="aspect-square relative cursor-pointer overflow-hidden" onClick={() => onPlay(beat)}>
        {beat.cover_art_url ? (
          <img src={beat.cover_art_url} alt={beat.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-600/20 to-amber-600/10 flex items-center justify-center">
            <Disc3 className="w-12 h-12 text-orange-400/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <div className={`w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center transform ${isPlaying ? 'scale-100' : 'scale-0 group-hover:scale-100'} transition-transform`}>
            {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
          </div>
        </div>
        {beat.is_free && (
          <div className="absolute top-2 right-2 bg-green-600/80 backdrop-blur-sm text-xs font-medium px-2 py-1 rounded-full text-white">FREE</div>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-sm truncate">{beat.title}</h4>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          {beat.genre && <span>{beat.genre}</span>}
          {beat.bpm && <span>{beat.bpm} BPM</span>}
          {beat.key && <span>{beat.key}</span>}
          {beat.duration > 0 && <span>{formatDuration(beat.duration)}</span>}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Headphones className="w-3 h-3" />{beat.plays_count || 0}</span>
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{beat.likes_count || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggleLike} className="text-muted-foreground hover:text-orange-400 transition-colors">
              <Heart className={`w-4 h-4 ${liked ? 'fill-orange-400 text-orange-400' : ''}`} />
            </button>
            <AddToPlaylistButton trackId={beat.beat_id} size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-purple-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PublicProducerPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const { playTrack, currentTrack, isPlaying: playerPlaying } = usePlayer();

  const [producer, setProducer] = useState(null);
  const [beats, setBeats] = useState([]);
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [activeTab, setActiveTab] = useState('beats');
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchProducer(); }, [handle]);

  const fetchProducer = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/producers/${handle}`, { headers });
      if (!res.ok) { navigate('/find-producers'); return; }
      const data = await res.json();
      setProducer(data);
      fetchBeats(data.user_id);
      fetchPosts(data.user_id);
      fetchVideos(data.user_id);

      if (isAuthenticated && user) {
        const fRes = await fetch(`${API}/users/${data.user_id}/follow-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (fRes.ok) {
          const fData = await fRes.json();
          setIsFollowing(fData.is_following);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBeats = async (userId) => {
    try {
      const res = await fetch(`${API}/producers/${userId}/beats?limit=50`);
      if (res.ok) { const data = await res.json(); setBeats(data.beats || []); }
    } catch {}
  };

  const fetchPosts = async (userId) => {
    try {
      const res = await fetch(`${API}/producers/${userId}/posts?limit=20`);
      if (res.ok) { const data = await res.json(); setPosts(data.posts || []); }
    } catch {}
  };

  const fetchVideos = async (userId) => {
    try {
      const res = await fetch(`${API}/producers/${userId}/videos?limit=20`);
      if (res.ok) { const data = await res.json(); setVideos(data.videos || []); }
    } catch {}
  };

  const toggleFollow = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const endpoint = isFollowing
        ? `${API}/users/${producer.user_id}/unfollow`
        : `${API}/users/${producer.user_id}/follow`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIsFollowing(!isFollowing);
        setProducer(p => ({ ...p, followers_count: (p.followers_count || 0) + (isFollowing ? -1 : 1) }));
      }
    } catch { toast.error('Failed to update follow'); }
  };

  const sendMessage = async () => {
    if (!contactMsg.trim() || !isAuthenticated) return;
    setSendingMsg(true);
    try {
      const res = await fetch(`${API}/messages/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: producer.user_id, content: contactMsg })
      });
      if (res.ok) {
        toast.success('Message sent!');
        setShowContact(false);
        setContactMsg('');
      } else { toast.error('Failed to send message'); }
    } catch { toast.error('Error sending message'); }
    finally { setSendingMsg(false); }
  };

  const playBeat = (beat) => {
    // Use global player for in-app playback
    playTrack({
      track_id: beat.beat_id,
      title: beat.title,
      artist_name: producer?.name || beat.producer_name || 'Producer',
      cover_url: beat.cover_art_url,
      audio_url: beat.audio_url,
      duration: beat.duration
    });
    // Record play
    fetch(`${API}/beats/${beat.beat_id}/play`, { method: 'POST' }).catch(() => {});
  };

  const shareProfile = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${producer.name} on DMV Soundstage`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Link copied!');
      }
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>
  );
  if (!producer) return (
    <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Producer not found</p></div>
  );

  const pp = producer;
  const isOwn = user?.user_id === pp.user_id;
  const tabCounts = {
    beats: beats.length,
    posts: posts.length,
    videos: videos.length,
  };

  return (
    <div className="min-h-screen pb-32" data-testid="producer-profile-page">
      {/* Banner */}
      <div className="h-44 md:h-56 relative bg-gradient-to-br from-orange-900/40 via-zinc-900 to-amber-900/20">
        {pp.banner_url && <img src={pp.banner_url} className="w-full h-full object-cover absolute inset-0" alt="" />}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
          className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm text-white hover:bg-background/60 z-10">
          <ChevronLeft className="w-5 h-5" />
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-14 relative z-10">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <div className="flex-shrink-0">
            {pp.avatar_url ? (
              <img src={pp.avatar_url} alt={pp.name} className="w-28 h-28 md:w-32 md:h-32 rounded-2xl object-cover border-4 border-border shadow-xl" />
            ) : (
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-orange-600 to-amber-700 flex items-center justify-center border-4 border-border shadow-xl">
                <Music className="w-14 h-14 text-foreground/80" />
              </div>
            )}
          </div>

          <div className="flex-1 pt-4 md:pt-6">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="producer-name">{pp.name}</h1>
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 font-semibold" data-testid="producer-badge">Producer</Badge>
              {pp.is_verified && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Verified</Badge>}
            </div>
            <p className="text-muted-foreground text-sm mb-2">{pp.handle ? `@${pp.handle}` : ""}</p>

            {pp.location && (
              <p className="text-muted-foreground text-sm flex items-center gap-1 mb-2">
                <MapPin className="w-3.5 h-3.5" />{pp.location}
              </p>
            )}

            {pp.bio && <p className="text-foreground/80 text-sm mb-3 max-w-xl">{pp.bio}</p>}

            {pp.genres?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {pp.genres.map((g, i) => (
                  <Badge key={i} variant="outline" className="text-xs border-border text-foreground/80">{g}</Badge>
                ))}
              </div>
            )}

            {pp.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {pp.tags.map((t, i) => (
                  <span key={i} className="text-xs text-orange-400/70 flex items-center gap-0.5">
                    <Tag className="w-3 h-3" />{t}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-5 text-sm text-muted-foreground mb-4">
              <span><strong className="text-white">{pp.followers_count || 0}</strong> followers</span>
              <span><strong className="text-white">{pp.beat_count || beats.length}</strong> beats</span>
              <span><strong className="text-white">{pp.total_plays || 0}</strong> plays</span>
            </div>

            {pp.work_with_me && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-3">Open to Work</Badge>
            )}

            {/* Actions */}
            {!isOwn && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="follow-producer-btn"
                  onClick={toggleFollow}
                  variant={isFollowing ? 'outline' : 'default'}
                  className={!isFollowing ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-border'}
                  size="sm"
                >
                  {isFollowing ? <><UserMinus className="w-4 h-4 mr-1.5" />Unfollow</> : <><UserPlus className="w-4 h-4 mr-1.5" />Follow</>}
                </Button>
                <Button data-testid="request-producer-btn" variant="outline" size="sm" className="border-border"
                  onClick={() => setShowContact(true)}>
                  <MessageSquare className="w-4 h-4 mr-1.5" />Send Request
                </Button>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={shareProfile}
                  data-testid="share-producer-btn">
                  {copied ? <Check className="w-4 h-4 mr-1.5 text-green-400" /> : <Share2 className="w-4 h-4 mr-1.5" />}
                  {copied ? 'Copied' : 'Share'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Contact Modal */}
        {showContact && (
          <div className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowContact(false)}>
            <Card className="w-full max-w-md bg-card border-border p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Send Request to {pp.name}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowContact(false)}><X className="w-4 h-4" /></Button>
              </div>
              <Textarea
                data-testid="contact-message-input"
                placeholder="Hey, I'd love to work with you on..."
                value={contactMsg}
                onChange={e => setContactMsg(e.target.value)}
                rows={4}
                className="bg-muted/50 border-border mb-3"
              />
              <Button data-testid="send-message-btn" className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={sendMessage} disabled={sendingMsg || !contactMsg.trim()}>
                {sendingMsg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-8 border-b border-border pb-px" data-testid="profile-tabs">
          {[
            { key: 'beats', label: 'Beats', icon: Disc3 },
            { key: 'posts', label: 'Posts', icon: MessageSquare },
            { key: 'videos', label: 'Videos', icon: Video },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-orange-500 text-orange-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground/80'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tabCounts[tab.key] > 0 && (
                <span className="text-[10px] bg-muted/30 px-1.5 py-0.5 rounded-full">{tabCounts[tab.key]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Beats */}
          {activeTab === 'beats' && (
            beats.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Disc3 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
                <p>No beats uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="beats-grid">
                {beats.map(beat => (
                  <BeatCard
                    key={beat.beat_id}
                    beat={beat}
                    onPlay={playBeat}
                    isPlaying={playerPlaying && currentTrack?.track_id === beat.beat_id}
                  />
                ))}
              </div>
            )
          )}

          {/* Posts */}
          {activeTab === 'posts' && (
            posts.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
                <p>No posts yet</p>
              </div>
            ) : (
              <div className="max-w-2xl space-y-4">
                {posts.map(post => (
                  <PostCard key={post.post_id} post={post} />
                ))}
              </div>
            )
          )}

          {/* Videos */}
          {activeTab === 'videos' && (
            videos.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Video className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
                <p>No videos yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {videos.map(video => (
                  <Link key={video.video_id} to={`/videos/${video.video_id}`}>
                    <Card className="bg-muted/20 border-border hover:border-orange-500/20 transition-all overflow-hidden">
                      {video.thumbnail_url && (
                        <img src={video.thumbnail_url} alt={video.title} className="w-full aspect-video object-cover" />
                      )}
                      <div className="p-3">
                        <h4 className="font-medium text-sm truncate">{video.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{video.views_count || 0} views</p>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
